import { useEffect } from "react";
import DavenportHeroSection from "./components/HeroSection";
import DavenportStickyNav from "./components/StickyNav";
import DavenportProcessingTimeSection from "./components/ProcessingTimeSection";
import DavenportServicesSection from "./components/ServicesSection";
import DavenportLocalAreaSection from "./components/LocalAreaSection";
import DavenportTestimonialsSection from "./components/TestimonialsSection";
import DavenportAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import DavenportComparisonSection from "./components/ComparisonSection";
import DavenportMistakesSection from "./components/MistakesSection";
import DavenportHowItWorksSection from "./components/HowItWorksSection";
import DavenportFAQSection, { faqs } from "./components/FAQSection";
import DavenportHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import DavenportFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function DavenportPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Davenport IA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/davenport/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Davenport, IA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Scott County and Quad Cities travelers.");
    setMeta("geo.placename", "Davenport, Iowa");
    setMeta("geo.region", "US-IA");
    setMeta("geo.position", "41.5236;-90.5776");
    setMeta("ICBM", "41.5236, -90.5776");
    setMeta("keywords", "expedited passport, emergency passport, Davenport passport, same day passport Davenport, fast passport Iowa, Quad Cities passport, Scott County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Iowa", item: "https://fastpassportcenter.com/iowa/" },
          { "@type": "ListItem", position: 3, name: "Davenport Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Davenport — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Davenport and Quad Cities travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Davenport",
          containedInPlace: {
            "@type": "State",
            name: "Iowa",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Davenport",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <DavenportHeroSection />
      <DavenportStickyNav />
      <DavenportProcessingTimeSection />
      <DavenportServicesSection />
      <DavenportLocalAreaSection />
      <DavenportTestimonialsSection />
      <DavenportAcceptanceFacilitiesSection />
      <DavenportComparisonSection />
      <DavenportMistakesSection />
      <DavenportHowItWorksSection />
      <DavenportFAQSection />
      <DavenportHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/davenport" state="IA" />
      <DavenportFinalCTA />
    </div>
  );
}